import React, { useContext, useState } from "react"
import { Col, Div, Row, Text } from "atomize"
import { motion } from "framer-motion"
import { toast } from "react-toastify"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faStar as faStarEmpty } from "@fortawesome/free-regular-svg-icons"
import {
	faBoxArchive,
	faBoxOpen,
	faClockRotateLeft,
	faStar,
} from "@fortawesome/free-solid-svg-icons"
import Container from "../atoms/Container"
import SecondaryButton from "../buttons/SecondaryButton"
import Divider from "../atoms/Divider"
import { theme } from "../settings/config"
import { profileContext } from "../providers/profile.provider"
import { schoolContext } from "../providers/school.provider"
import SchoolAvatar from "../school/SchoolAvatar"
import {
	ARCHIVED_SCHOOL,
	PRIMARY_SCHOOL,
	activeSchoolIDs,
	archivedSchoolIDs,
	isPrimarySchool,
} from "../utils/school.utils"

// I circoli di chi insegna in più posti, messi in ordine: quale è il
// principale — quello con cui si apre l'app — e quali sono archiviati.
//
// Archiviare non è uscire. Nel circolo si resta, e con lui le lezioni, gli
// allievi e i conti di quando ci si lavorava: sparisce solo dal selettore e
// dalle lezioni degli altri circoli. Da qui lo si riguarda quando serve, e
// lo si rimette in uso.
export function MySchoolsModal({ closeModal }) {
	const { coach } = useContext(profileContext)
	const { schools, selectedSchool, setSelectedSchool, setSchoolPlacement } =
		useContext(schoolContext)

	// il circolo su cui si sta salvando: uno alla volta
	const [savingID, setSavingID] = useState(null)

	const knownIDs = Object.keys(schools ?? {})
	const active = activeSchoolIDs(coach, knownIDs)
	const archived = archivedSchoolIDs(coach, knownIDs)

	const nameOf = schoolID => schools[schoolID]?.schoolName ?? "il circolo"

	const place = async (schoolID, placement, message) => {
		if (savingID) return
		setSavingID(schoolID)
		try {
			await setSchoolPlacement(schoolID, placement)
			toast.success(message, { position: "top-center" })
		} catch (err) {
			toast.error(err.message || "Non sono riuscito a salvarlo", { position: "top-center" })
		} finally {
			setSavingID(null)
		}
	}

	const togglePrimary = schoolID =>
		isPrimarySchool(coach, schoolID)
			? place(schoolID, null, `${nameOf(schoolID)} non è più il principale`)
			: place(
					schoolID,
					PRIMARY_SCHOOL,
					`${nameOf(schoolID)} è il principale: l'app si apre lì`,
				)

	const archive = schoolID =>
		place(
			schoolID,
			ARCHIVED_SCHOOL,
			`${nameOf(schoolID)} archiviato: lo storico lo ritrovi qui`,
		)

	const restore = schoolID => place(schoolID, null, `${nameOf(schoolID)} è di nuovo in uso`)

	const look = schoolID => {
		setSelectedSchool(schoolID)
		closeModal()
	}

	const action = ({ icon, label, color = theme.colors.primary, onClick, disabled }) => (
		<motion.button
			type="button"
			whileTap={{ scale: 0.94 }}
			disabled={disabled}
			onClick={onClick}
			style={{
				display: "flex",
				alignItems: "center",
				gap: "0.35rem",
				padding: "0.35rem 0.7rem",
				border: 0,
				borderRadius: "999px",
				background: theme.colors.background,
				color,
				cursor: disabled ? "default" : "pointer",
				opacity: disabled ? 0.5 : 1,
				fontSize: "0.8125rem",
				fontWeight: 600,
				whiteSpace: "nowrap",
			}}>
			<FontAwesomeIcon icon={icon} />
			{label}
		</motion.button>
	)

	const row = (schoolID, subtitle, actions) => (
		<Div
			key={schoolID}
			d="flex"
			align="center"
			p={{ y: "0.6rem" }}
			style={{ gap: "0.75rem" }}
			data-testid={`my-school-${schoolID}`}>
			<SchoolAvatar school={schools[schoolID]} size={36} />
			<Div style={{ flex: 1, minWidth: 0 }}>
				<Text
					textWeight={schoolID === selectedSchool ? "bold" : "normal"}
					style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
					{nameOf(schoolID)}
				</Text>
				{subtitle && (
					<Text textSize="caption" textColor={theme.colors.text_light}>
						{subtitle}
					</Text>
				)}
			</Div>
			<Div d="flex" flexWrap="wrap" justify="flex-end" style={{ gap: "0.4rem" }}>
				{actions}
			</Div>
		</Div>
	)

	const sectionTitle = text => (
		<Text
			textSize="caption"
			textWeight="bold"
			textColor={theme.colors.text_light}
			m={{ t: "1rem" }}
			style={{ textTransform: "uppercase", letterSpacing: "0.04em" }}>
			{text}
		</Text>
	)

	return (
		<Container>
			<Div m={{ b: "0.5rem" }} textWeight="bold" textSize="title">
				I tuoi circoli
			</Div>
			<Text textColor={theme.colors.gunmetal}>
				Il principale è quello con cui si apre l'app. Uno dove non lavori più lo archivi:
				sparisce dal selettore, ma lezioni, allievi e conti restano, e li riguardi da qui.
			</Text>

			{sectionTitle("In uso")}
			{active.map((schoolID, i) => {
				const isPrimary = isPrimarySchool(coach, schoolID)
				return (
					<React.Fragment key={schoolID}>
						{i > 0 && <Divider w="2px solid" borderColor={theme.colors.background} />}
						{row(
							schoolID,
							isPrimary ? "L'app si apre qui" : null,
							<>
								{action({
									icon: isPrimary ? faStar : faStarEmpty,
									label: isPrimary ? "Principale" : "Rendi principale",
									disabled: savingID !== null,
									onClick: () => togglePrimary(schoolID),
								})}
								{/* l'ultimo in uso non si archivia: l'app si
								    aprirebbe su un circolo nascosto */}
								{active.length > 1 &&
									action({
										icon: faBoxArchive,
										label: "Archivia",
										color: theme.colors.text_light,
										disabled: savingID !== null,
										onClick: () => archive(schoolID),
									})}
							</>,
						)}
					</React.Fragment>
				)
			})}

			{archived.length > 0 && (
				<>
					{sectionTitle("Archiviati")}
					{archived.map((schoolID, i) => (
						<React.Fragment key={schoolID}>
							{i > 0 && <Divider w="2px solid" borderColor={theme.colors.background} />}
							{row(
								schoolID,
								schoolID === selectedSchool ? "Lo stai guardando" : null,
								<>
									{schoolID !== selectedSchool &&
										action({
											icon: faClockRotateLeft,
											label: "Riguarda",
											color: theme.colors.gunmetal,
											onClick: () => look(schoolID),
										})}
									{action({
										icon: faBoxOpen,
										label: "Rimetti in uso",
										disabled: savingID !== null,
										onClick: () => restore(schoolID),
									})}
								</>,
							)}
						</React.Fragment>
					))}
				</>
			)}

			<Div m={{ x: "1rem" }}>
				<Row m={{ t: "1rem" }}>
					<Col p="0">
						<SecondaryButton
							m="auto"
							textColor={theme.colors.gunmetal}
							textSize="subheader"
							text="Chiudi"
							action={closeModal}
						/>
					</Col>
				</Row>
			</Div>
		</Container>
	)
}

export default MySchoolsModal
